/**
 * AcceptInvitation.jsx
 *
 * Lands here from an invitation email: /accept-invite?token=XXX
 * Flow:
 *   1. Verify token via GET /api/invites/personal/verify
 *   2. Show team + role confirmation
 *   3. Ask only for password (email/name/role pre-filled by invitation)
 *   4. POST /api/invites/personal/accept → login and explicit completion
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, AlertCircle, CheckCircle2, Users, User, Heart, ShieldCheck, Mail, AlertTriangle, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import LoginLanguageSwitcher from '../components/auth/LoginLanguageSwitcher';
import SocialAuthButtons from '../components/auth/SocialAuthButtons';
import { requestMagicLink } from '../lib/api';
import { invitationIdentity, invitationError, invitationNextStep } from '../lib/invitationFlow';

const API_URL = ''; // Same-origin /api works in Preview and production.

export default function AcceptInvitation() {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, loginUser, refreshUser, logout } = useAuth();

  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error' | 'submitting' | 'done'
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [nextStep, setNextStep] = useState('');
  const [retry, setRetry] = useState(0);
  const [loginHelpSent, setLoginHelpSent] = useState(false);
  const [syncWarning, setSyncWarning] = useState('');
  const lifecycle = useRef(null);
  const operation = useRef(false);
  const pageToken = searchParams.get('token') || '';

  useEffect(() => {
    const run = {};
    lifecycle.current = run;
    operation.current = false;
    setStatus('loading'); setInvite(null); setPassword(''); setConfirmPassword(''); setAgreed(false);
    setLoginHelpSent(false); setSyncWarning(''); setErrorMsg(''); setNextStep('');
    const load = async () => {
      if (!pageToken) {
        setStatus('error'); setErrorMsg(t('auth:invite.missing_token_error'));
        setNextStep('Öppna hela länken från ditt senaste inbjudningsmejl.'); return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/invites/personal/verify`, { params: { token: pageToken } });
        if (lifecycle.current !== run) return;
        setInvite(res.data); setStatus('ready');
      } catch (error) {
        if (lifecycle.current !== run) return;
        setStatus('error'); setErrorMsg(invitationError(error, t('auth:invite.invalid_error')));
        setNextStep(invitationNextStep(error));
      }
    };
    load();
    return () => { if (lifecycle.current === run) lifecycle.current = null; };
  }, [pageToken, retry, t]);

  const accept = async (asCurrentUser) => {
    if (operation.current || status !== 'ready' || authLoading || !invite) return;
    if (asCurrentUser && (!user || invitationIdentity(invite, user).mismatch)) return;
    operation.current = true;
    const run = lifecycle.current;
    const isCurrent = () => run !== null && lifecycle.current === run;
    const sessionToken = localStorage.getItem('token');
    let accepted = false;
    setStatus('submitting');
    try {
      const res = asCurrentUser
        ? await axios.post(`${API_URL}/api/invites/personal/accept-as-user`, { token: pageToken },
            { headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {} })
        : await axios.post(`${API_URL}/api/invites/personal/accept`, {
            token: pageToken, password, policy_accepted: true, gdpr_consent: true });
      accepted = true;
      if (!isCurrent()) return;
      if (localStorage.getItem('token') !== sessionToken) {
        throw new Error('Inbjudan accepterades, men din inloggning ändrades. Öppna appen och kontrollera vilket konto du använder.');
      }
      if (asCurrentUser) {
        const refreshed = await refreshUser();
        if (!refreshed) throw new Error('refresh_failed');
      } else {
        await loginUser(res.data.access_token, res.data.user);
        if (localStorage.getItem('token') !== res.data.access_token) throw new Error('session_changed');
      }
      if (isCurrent()) setStatus('done');
    } catch (error) {
      if (!isCurrent()) return;
      if (accepted) {
        setSyncWarning('Inbjudan är accepterad, men kontovyn kunde inte uppdateras. Öppna appen igen. Skicka inte in inbjudan en gång till.');
        setStatus('done');
      } else {
        setStatus('ready');
        toast.error(invitationError(error, t('auth:invite.generic_error')));
      }
    } finally {
      if (isCurrent()) operation.current = false;
    }
  };

  const handleAccept = async (event) => {
    event.preventDefault();
    if (operation.current) return;
    if (!password || (!invite?.existing_account && password.length < 6)) {
      toast.error(t('auth:invite.password_min_toast')); return;
    }
    if (!invite?.existing_account && password !== confirmPassword) {
      toast.error(t('auth:invite.password_mismatch_toast')); return;
    }
    if (!agreed) { toast.error(t('auth:invite.must_agree_toast')); return; }
    await accept(false);
  };
  const handleAcceptAsCurrentUser = () => accept(true);

  const requestLoginHelp = async () => {
    if (operation.current || !invite?.existing_account) return;
    const identity = invitationIdentity(invite, null);
    const identifier = identity.usernameFlow ? invite.username : identity.loginEmail;
    if (!identifier) return;
    operation.current = true; setStatus('submitting');
    const run = lifecycle.current;
    try {
      await requestMagicLink(identifier, undefined, window.location.origin);
      if (lifecycle.current === run) setLoginHelpSent(true);
    } catch (error) {
      if (lifecycle.current === run) toast.error(invitationError(error, 'Kunde inte begära inloggningshjälp. Försök igen.'));
    } finally {
      if (lifecycle.current === run) { operation.current = false; setStatus('ready'); }
    }
  };

  if (status === 'loading' || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-start justify-center py-8 px-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h1 className="font-heading text-4xl font-bold text-[rgb(var(--tn-text-ffffff))]">{t('common:brand.name')}</h1>
          </div>
          <Card className="bg-card border-border" data-testid="accept-invite-error-card">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-destructive/15 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <h2 className="font-heading text-2xl text-[rgb(var(--tn-text-ffffff))]">{t('auth:invite.invite_not_working_title')}</h2>
              <p className="text-muted-foreground text-sm">{errorMsg}</p>
              <p className="text-sm">{nextStep}</p>
              <Button variant="outline" onClick={() => setRetry((value) => value + 1)}>Kontrollera länken igen</Button>
              <div className="pt-2">
                <Link to="/login">
                  <Button className="w-full" data-testid="accept-invite-login-btn">{t('auth:login.go_to_login')}</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
          <LoginLanguageSwitcher />
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="min-h-screen bg-background flex items-start justify-center py-8 px-4">
        <div className="w-full max-w-md">
          <Card className="bg-card border-border">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <h2 className="font-heading text-2xl text-[rgb(var(--tn-text-ffffff))]">{t('auth:invite.welcome_toast', { team: invite?.team_name })}</h2>
              <p className="text-muted-foreground text-sm">Inbjudan är accepterad. Dina övriga lag och roller finns kvar.</p>
              {syncWarning && <p role="alert" className="text-sm text-[rgb(var(--tn-text-fcd34d))]">{syncWarning}</p>}
              <Button className="w-full" onClick={() => window.location.assign('/')}>Öppna appen</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // status === 'ready' or 'submitting'
  // V13 — Two flows: logged-in user gets a single "Gå med i laget" button,
  // anonymous user gets the create-account form.
  // P3 — canonical login email, not delivery address; applies to ALL personal invites.
  // When the invite is for a different
  // identity than the logged-in user we BLOCK the "join" path and force a
  // logout-and-activate handoff. See accept-as-user backend guard for the
  // server-side counterpart.
  const isLoggedIn = !!user;
  const { loginEmail: inviteEmail, mismatch: identityMismatch } = invitationIdentity(invite, user);

  // Role-specific CTA label. The generic phrase "Gå med i laget" is
  // reserved for the team-code join flow (TeamSelector). For personal
  // invitations we always surface the activation target by role so the
  // accepting user understands exactly which account they are creating
  // or activating.
  const activateCtaLabel = (() => {
    switch (invite.role) {
      case 'player':
        return t('auth:invite.activate_player');
      case 'parent':
        return t('auth:invite.activate_parent');
      case 'head_coach':
      case 'assistant_coach':
      case 'coach':
        return t('auth:invite.activate_coach');
      default:
        return t('auth:invite.activate_account');
    }
  })();

  const handleLogoutAndReturn = async () => {
    if (operation.current) return;
    operation.current = true; setStatus('submitting');
    const run = lifecycle.current;
    try {
      await logout();
      if (lifecycle.current !== run) return;
      if (localStorage.getItem('token')) throw new Error('Session still present');
      setRetry((value) => value + 1);
    } catch {
      if (lifecycle.current === run) toast.error('Kunde inte byta konto. Försök igen.');
    } finally {
      if (lifecycle.current === run) { operation.current = false; setStatus('ready'); }
    }
  };

  // Display title — role-based, never the generic "Du har blivit inbjuden"
  // / "Gå med i laget" anymore. Those phrases are reserved for non-personal
  // contexts.
  const existingCta = invite.role === 'parent' && invite.linked_child ? 'Bekräfta barnkopplingen' : 'Acceptera inbjudan';
  const actionLabel = invite.existing_account || isLoggedIn ? existingCta : activateCtaLabel;
  const pageTitle = identityMismatch ? t('auth:invite.identity_mismatch_title') : actionLabel;
  const teamContext = [invite.org_name, invite.team_name].filter(Boolean).join(' · ');

  return (
    <div className="min-h-screen bg-background flex items-start justify-center py-8 px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-1">
          <h1 className="font-heading text-4xl font-bold text-[rgb(var(--tn-text-ffffff))]">{t('common:brand.name')}</h1>
          {teamContext && (
            <p className="text-xs text-muted-foreground tracking-wide" data-testid="brand-team-context">{teamContext}</p>
          )}
        </div>

        <Card className="bg-card border-border" data-testid="accept-invite-card">
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mb-2">
              <Users className="w-7 h-7 text-primary" />
            </div>
            <CardTitle className="font-heading text-2xl" data-testid="accept-invite-title">
              {pageTitle}
            </CardTitle>
            {invite.invited_by && !identityMismatch && (
              <p className="text-xs text-muted-foreground mt-1">{t('auth:invite.invited_by', { name: invite.invited_by })}</p>
            )}
          </CardHeader>
          <CardContent>
            {/* Explicit activation target — the email recipient is not
                necessarily the account holder. */}
            <ActivationTargetBlock invite={invite} t={t} />

            {identityMismatch ? (
              <div className="space-y-4 mt-4" data-testid="accept-invite-identity-mismatch">
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-2">
                  <div className="flex items-start gap-2 text-[rgb(var(--tn-text-fcd34d))]">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="space-y-1.5 text-sm leading-relaxed">
                      <p>
                        {t('auth:invite.identity_mismatch_p1_prefix')} <strong className="text-[rgb(var(--tn-text-ffffff))]">{user.name || user.email}</strong>.
                      </p>
                      <p>
                        {t('auth:invite.identity_mismatch_p2_prefix')} <strong className="text-[rgb(var(--tn-text-ffffff))]">{invite?.activation_target?.name || invite?.name}</strong>.
                      </p>
                      <p className="text-[rgb(var(--tn-text-fde68a))]/90">
                        {t('auth:invite.identity_mismatch_p3')}
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleLogoutAndReturn}
                  disabled={status === 'submitting'}
                  className="w-full bg-primary text-primary-foreground"
                  data-testid="accept-invite-logout-and-activate"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('auth:invite.logout_and_activate')}
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full"
                  data-testid="accept-invite-cancel"
                >
                  {t('auth:invite.cancel')}
                </Button>
              </div>
            ) : isLoggedIn ? (
              <div className="space-y-4 mt-4" data-testid="accept-invite-as-user">
                <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-sm">
                  <div className="text-muted-foreground text-xs mb-1">{t('auth:invite.logged_in_as')}</div>
                  <div className="font-medium text-[rgb(var(--tn-text-ffffff))]">{user.name || user.email}</div>
                </div>
                <Button
                  onClick={handleAcceptAsCurrentUser}
                  className="w-full bg-primary text-primary-foreground"
                  disabled={status === 'submitting'}
                  data-testid="accept-invite-join-btn"
                >
                  {status === 'submitting' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {actionLabel}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  {t('auth:invite.not_you_prefix')}{' '}
                  <button type="button" onClick={handleLogoutAndReturn} disabled={status === 'submitting'} className="text-primary underline">
                    Byt konto
                  </button>
                </p>
              </div>
            ) : (
              <form onSubmit={handleAccept} className="space-y-4 mt-4" data-testid="accept-invite-form">
              <SocialAuthButtons next={`/accept-invite?token=${encodeURIComponent(pageToken)}`} disabled={status === 'submitting'} />
              <div className="space-y-2">
                <Label>{t('auth:invite.name_label')}</Label>
                <Input value={invite.name} disabled className="bg-muted/40" />
              </div>
              {invite.login_mode === 'username' && invite.username ? (
                <div className="space-y-2" data-testid="accept-username-field">
                  <Label>{t('auth:invite.username_label')}</Label>
                  <Input value={invite.username} disabled className="bg-muted/40 font-mono" />
                  <p className="text-[11px] text-muted-foreground">
                    {t('auth:invite.username_hint_prefix')}{invite.email}{t('auth:invite.username_hint_suffix')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t('auth:invite.email_label')}</Label>
                  <Input value={inviteEmail} disabled className="bg-muted/40" />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">{invite.existing_account
                  ? t('auth:invite.existing_password', { defaultValue: 'Ditt befintliga lösenord' })
                  : t('auth:invite.choose_password')}</Label>
                {invite.existing_account && (
                  <p className="text-xs text-muted-foreground" data-testid="accept-invite-existing-account">
                    Kontot finns redan. Använd ditt befintliga lösenord. Kontot och lösenordet behålls när du accepterar inbjudan.
                  </p>
                )}
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={invite.existing_account ? 'current-password' : 'new-password'}
                  placeholder={invite.existing_account ? '' : t('auth:invite.password_placeholder')}
                  required
                  minLength={invite.existing_account ? undefined : 6}
                  disabled={status === 'submitting'}
                  data-testid="accept-invite-password"
                />
              </div>
              {invite.existing_account && <div className="rounded-lg border border-border p-3 space-y-2 text-sm">
                <p>Saknar du lösenord eller har glömt det?</p>
                <Button type="button" variant="outline" className="w-full" disabled={status === 'submitting' || loginHelpSent}
                  onClick={requestLoginHelp}>Begär inloggningslänk</Button>
                {loginHelpSent && <p role="status">Om inloggning är tillgänglig skickas en länk till kontots registrerade adress.
                  Logga in via mejlet och återvänd sedan till den här inbjudan för att bekräfta laget eller barnkopplingen.</p>}
                {loginHelpSent && <Button type="button" variant="ghost" className="w-full" onClick={() => window.location.reload()}>
                  Jag har loggat in – fortsätt
                </Button>}
              </div>}
              {!invite.existing_account && <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth:invite.confirm_password')}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="accept-invite-confirm"
                  autoComplete="new-password"
                />
              </div>}

              <div className="flex items-start space-x-2 pt-2">
                <Checkbox
                  id="agree"
                  checked={agreed}
                  onCheckedChange={setAgreed}
                  data-testid="accept-invite-agree"
                />
                <label htmlFor="agree" className="text-xs text-muted-foreground leading-relaxed">
                  {t('auth:invite.agree_terms')}
                </label>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground"
                disabled={status === 'submitting' || !agreed}
                data-testid="accept-invite-submit"
              >
                {status === 'submitting' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {actionLabel}
              </Button>
            </form>
            )}
            <div className="pt-3 text-center">
              <Link
                to={`/feedback?topic=invite&token=${searchParams.get('token') || ''}`}
                className="text-xs text-muted-foreground/80 underline hover:text-muted-foreground"
                data-testid="accept-invite-not-me"
              >
                {t('auth:invite.not_correct')}
              </Link>
            </div>
          </CardContent>
        </Card>

        <LoginLanguageSwitcher />
      </div>
    </div>
  );
}

/** Compact identity block — the email recipient is shown separately from
 *  the activation target so it's crystal clear what account is being created. */
function ActivationTargetBlock({ invite, t }) {
  const target = invite?.activation_target;
  const child = invite?.linked_child;
  const isParent = invite?.role === 'parent';
  const isUsernameFlow = invite?.login_mode === 'username' && invite?.username;
  const Icon = isParent ? Heart : (invite?.role === 'player' ? User : ShieldCheck);
  const accent = isParent ? 'text-fuchsia-300' : invite?.role === 'player' ? "text-[rgb(var(--tn-text-6ee7b7))]" : 'text-primary';
  return (
    <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-3" data-testid="activation-target-block">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.13em] text-muted-foreground">{t('auth:invite.target_heading')}</p>

      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full bg-muted/30 flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-[rgb(var(--tn-text-ffffff))] truncate" data-testid="activation-target-name">
            {target?.name || invite?.name}
          </p>
          <p className="text-xs text-muted-foreground">
            <span className="text-foreground/80 font-medium">{target?.account_type || invite?.role_label}</span>
            {t('auth:invite.role_label')}
            <span className="text-foreground/80 font-medium">{target?.role_label || invite?.role_label}</span>
          </p>
          {child && (
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-fuchsia-300/90 font-medium">{t('auth:invite.child_label')}</span>{' '}
              <span className="text-foreground/85 font-medium">{child.name}</span>
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            <span className="text-foreground/80 font-medium">{t('auth:invite.team_label')}</span>{' '}
            <span className="text-foreground/85">{invite?.team_name}</span>
          </p>
        </div>
      </div>

      {isUsernameFlow ? (
        <div className="pt-2 border-t border-border/50 space-y-1">
          <p className="text-[10.5px] font-semibold uppercase tracking-wide text-[rgb(var(--tn-text-6ee7b7))]/90">{t('auth:invite.login_heading')}</p>
          <p className="text-sm font-mono text-foreground/90" data-testid="activation-username">{invite.username}</p>
          <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
            <Mail className="w-3 h-3 mt-0.5 shrink-0" />
            <span><span className="text-muted-foreground/80">{t('auth:invite.delivered_to')}</span> <span className="text-foreground/70">{invite.email}</span></span>
          </p>
        </div>
      ) : (
        <div className="pt-2 border-t border-border/50 flex items-center gap-2 text-[11px] text-muted-foreground">
          <Mail className="w-3 h-3 shrink-0" />
          <span className="truncate"><span className="text-muted-foreground/80">{t('auth:invite.recipient')}</span> <span className="text-foreground/80">{invite?.email}</span></span>
        </div>
      )}
    </div>
  );
}
